import {Component, effect, inject, Input, signal} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {IUser} from '../../../../features/user-access/models';
import {Store} from '@ngrx/store';
import {
  resetUserPassword,
  updateUserProfileDetails,
} from '../../../../features/user-access/user-store/user.actions';
import {
  selectIsUserProfileUpdating,
  selectLastSuccessfulProfileUpdate,
} from '../../../../features/user-access/user-store/user.selectors';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {compressImageToBase64} from '../../../../shared/utils/image/image.utils';
import {PHONE_PATTERN_REGEX} from '../../../../shared/utils/form/form.utils';
import {formatLastSeenDaysAgo} from '../../../../features/user-access/utils/user-access-date.utils';

export interface IUserProfileConfigDismissData {
  confirmed: boolean;
  user?: Partial<IUser>;
}

@Component({
  selector: 'app-user-profile-config',
  templateUrl: './user-profile-config.component.html',
  styleUrls: ['./user-profile-config.component.scss'],
  standalone: false,
})
export class UserProfileConfigComponent {
  @Input() userStatus?: string;
  @Input() lastSeen?: Date | string | {toDate(): Date} | {seconds: number; nanoseconds?: number} | null;

  private readonly modalController = inject(ModalControllerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);

  profileForm: FormGroup;
  protected readonly isSaving = signal(false);
  protected readonly isHoveringProfileImage = signal(false);
  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;

  private baselineUser: IUser | null = null;
  private saveRequestedForUserId: string | null = null;
  private readonly isProfileUpdating = toSignal(
    this.store.select(selectIsUserProfileUpdating),
    {initialValue: false},
  );
  private readonly lastProfileUpdate = toSignal(
    this.store.select(selectLastSuccessfulProfileUpdate),
    {initialValue: null},
  );

  constructor() {
    this.profileForm = this.formBuilder.group({
      pp: [''],
      username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30)]],
      firstname: [''],
      lastname: [''],
      cellphone: ['', [Validators.pattern(PHONE_PATTERN_REGEX)]],
      email: [{value: '', disabled: true}],
    });

    effect(() => {
      if (!this.saveRequestedForUserId || this.isProfileUpdating()) {
        return;
      }

      const update = this.lastProfileUpdate();
      if (update?.id === this.saveRequestedForUserId) {
        this.saveRequestedForUserId = null;
        this.isSaving.set(false);
        void this.modalController.dismiss({
          confirmed: true,
          user: update,
        } satisfies IUserProfileConfigDismissData);
        return;
      }

      if (!this.isProfileUpdating() && this.isSaving()) {
        this.saveRequestedForUserId = null;
        this.isSaving.set(false);
      }
    });
  }

  get userId(): string {
    return this.baselineUser?.id ?? '';
  }

  get profileInitials(): string {
    const firstname = this.profileForm?.get('firstname')?.value?.trim() ?? '';
    const lastname = this.profileForm?.get('lastname')?.value?.trim() ?? '';
    if (firstname || lastname) {
      return `${firstname.charAt(0) ?? ''}${lastname.charAt(0) ?? ''}`.toUpperCase();
    }
    const username = this.profileForm?.get('username')?.value?.trim() ?? '';
    return username ? username.charAt(0).toUpperCase() : '?';
  }

  get isSaveDisabled(): boolean {
    return this.isSaving() || this.profileForm.invalid || this.profileForm.pristine;
  }

  get lastSeenDisplayText(): string {
    const formatted = formatLastSeenDaysAgo(this.lastSeen ?? null);
    return formatted ? `Last seen ${formatted}` : 'Never logged in';
  }

  get userEmail(): string {
    return (this.baselineUser?.contactDetails?.email ?? this.baselineUser?.id ?? '').trim();
  }

  @Input() set otherUser(userData: IUser) {
    if (!userData) {
      return;
    }
    this.initializeFromUser(userData);
  }

  onChangeProfilePictureClick(): void {
    document.getElementById('userProfileConfigPictureInput')?.click();
  }

  async onProfilePictureChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input?.files?.length) {
      return;
    }
    const compressedBase64 = await compressImageToBase64(input.files[0], 120, 120, 0.5);
    if (compressedBase64) {
      this.profileForm.controls['pp'].setValue(compressedBase64);
      this.profileForm.markAsDirty();
    }
  }

  onResetPasswordClick(): void {
    if (this.userEmail) {
      this.store.dispatch(resetUserPassword({userEmail: this.userEmail}));
    }
  }

  async onCancel(): Promise<void> {
    await this.modalController.dismiss({confirmed: false} satisfies IUserProfileConfigDismissData);
  }

  onSubmit(): void {
    if (this.isSaveDisabled || !this.baselineUser) {
      return;
    }

    const formValue = this.profileForm.getRawValue();
    const data: Partial<IUser> = {id: this.baselineUser.id};
    let hasChanges = false;

    const nextUsername = String(formValue.username ?? '').trim();
    if (nextUsername && nextUsername !== (this.baselineUser.userName ?? '').trim()) {
      data.userName = nextUsername;
      hasChanges = true;
    }

    const nextPp = String(formValue.pp ?? '');
    if (nextPp && nextPp !== (this.baselineUser.pp ?? '')) {
      data.pp = nextPp;
      hasChanges = true;
    }

    const nextFirstname = String(formValue.firstname ?? '').trim();
    const nextLastname = String(formValue.lastname ?? '').trim();
    const currentFirstname = this.baselineUser.personalDetails?.firstname?.trim() ?? '';
    const currentLastname = this.baselineUser.personalDetails?.lastname?.trim() ?? '';
    if (nextFirstname !== currentFirstname || nextLastname !== currentLastname) {
      data.personalDetails = {
        ...(nextFirstname ? {firstname: nextFirstname} : {}),
        ...(nextLastname ? {lastname: nextLastname} : {}),
      };
      hasChanges = true;
    }

    const nextCell = String(formValue.cellphone ?? '').trim();
    const currentCell = this.baselineUser.contactDetails?.cellphone?.trim() ?? '';
    if (nextCell !== currentCell) {
      data.contactDetails = {
        ...(nextCell ? {cellphone: nextCell} : {}),
      };
      hasChanges = true;
    }

    if (!hasChanges) {
      void this.onCancel();
      return;
    }

    this.isSaving.set(true);
    this.saveRequestedForUserId = this.baselineUser.id;
    this.store.dispatch(updateUserProfileDetails.updateUserProfile({data}));
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.profileForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  onFieldBlur(controlName: string): void {
    this.profileForm.get(controlName)?.markAsTouched();
  }

  private initializeFromUser(user: IUser): void {
    this.baselineUser = {...user};
    this.profileForm.reset({
      pp: user.pp ?? '',
      username: user.userName ?? '',
      firstname: user.personalDetails?.firstname ?? '',
      lastname: user.personalDetails?.lastname ?? '',
      cellphone: user.contactDetails?.cellphone ?? '',
      email: user.contactDetails?.email ?? user.id ?? '',
    });
    this.profileForm.markAsPristine();
  }
}
